import { useMemo, useEffect, useRef } from 'react';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';

import ConfigurationDetails from './ConfigurationDetails/ConfigurationDetails';
import { useConfigurationActions } from './hooks/useConfigurationActions';
import { findNodeInTree } from './helpers/utils';
import { ConfigurationsContextType } from './ConfigurationsPage';

export default function ConfigurationDetailsPage() {
  const { nodeId } = useParams<{ nodeId?: string }>();
  const navigate = useNavigate();
  const {
    treeData,
    setHasUnsavedChanges,
    clearUnsavedChanges,
    setSaveChangesCallback,
    setCurrentNodeName,
  } = useOutletContext<ConfigurationsContextType>();

  const saveFormRef = useRef<(() => Promise<void>) | null>(null);

  const foundNode = useMemo(() => {
    if (!treeData || !nodeId) {
      return null;
    }
    return findNodeInTree(treeData, nodeId);
  }, [treeData, nodeId]);

  const selectedNode = useMemo(() => {
    return {
      id: foundNode?.id ?? null,
      name: foundNode?.name ?? null,
      type: (foundNode?.type as string) ?? null,
      level: foundNode?.level,
      clusterId: foundNode?.clusterId,
      instanceId: foundNode?.instanceId,
      region: foundNode?.region, // The tree data might have this if it's an instance node
      accountId: foundNode?.accountId,
    };
  }, [foundNode]);

  const { handleSave, handleRevertField, handleRevertAll } = useConfigurationActions(
    selectedNode,
    clearUnsavedChanges
  );

  useEffect(() => {
    setSaveChangesCallback(() => async () => {
      if (saveFormRef.current) {
        await saveFormRef.current();
      }
    });
    return () => setSaveChangesCallback(null);
  }, [setSaveChangesCallback]);

  useEffect(() => {
    if (selectedNode.name) {
      setCurrentNodeName(selectedNode.name);
    }
  }, [selectedNode.name, setCurrentNodeName]);

  const handleNavigate = (id: string | null) => {
    // Used by AddNewConfigurationModal's onFinish
    if (!id) {
      return;
    }
    navigate(`/settings/configurations/${id}`);
    // Changes are cleared by navigation in layout or implicitly by unmount?
    // Layout handles clearUnsavedChanges on explicit nav.
    // But here we are triggering nav from child.
    clearUnsavedChanges();
  };

  if (!nodeId) {
    return (
      <div className="flex items-center justify-center w-full h-full text-primary-600">
        Select a configuration to view details
      </div>
    );
  }

  if (!foundNode) {
    // If tree is loading, Layout handles loader. If tree is loaded and node not found:
    return (
      <div className="flex items-center justify-center w-full h-full text-primary-300">
        Configuration not found
      </div>
    );
  }

  return (
    <ConfigurationDetails
      nodeData={{
        id: selectedNode.id,
        name: selectedNode.name,
        type: selectedNode.type,
        level: selectedNode.level,
        instanceId: selectedNode.instanceId,
        region: selectedNode.region, // Note: ConfigTreeNode might need to ensure these exist
        accountId: selectedNode.accountId,
      }}
      selectNode={handleNavigate}
      onSave={handleSave}
      onRevertField={handleRevertField}
      onRevertAll={handleRevertAll}
      onHasChangesChange={setHasUnsavedChanges}
      onSaveFormReady={(saveFn) => {
        saveFormRef.current = saveFn;
      }}
    />
  );
}
